// Inspired by: https://github.com/ndelvalle/v-click-outside/blob/1f184835f9c17d2bc6334b9dd2fb300b51183bea/src/v-click-outside.js

import type { DirectiveBinding, ObjectDirective } from 'vue';

const HANDLERS_PROPERTY = '__CLICK_OUTSIDE_HANDLERS__';

// Define the internal handler type
type ClickOutsideHandler = {
  event: keyof DocumentEventMap;
  handler: (e: Event) => void;
};

// Extend HTMLElement to include our custom property
interface ClickOutsideElement extends HTMLElement {
  [HANDLERS_PROPERTY]?: ClickOutsideHandler[];
}

function initialize(
  el: ClickOutsideElement,
  handler: (e: Event) => void,
): void {
  if (typeof handler !== 'function') {
    throw new Error('v-click-outside: Handler must be a function');
  }

  let lastMousedownInside = false;
  let lastMouseupInside = false;

  const supportsTouch = 'ontouchstart' in window;

  el[HANDLERS_PROPERTY] = [
    {
      event: supportsTouch ? 'touchstart' : 'mousedown',
      handler: (e: Event) => {
        lastMousedownInside = el.contains(e.target as Node);
      },
    },
    {
      event: supportsTouch ? 'touchend' : 'mouseup',
      handler: (e: Event) => {
        lastMouseupInside = el.contains(e.target as Node);
        if (!lastMousedownInside && !lastMouseupInside) {
          handler(e);
        }
      },
    },
  ];

  setTimeout(() => {
    el[HANDLERS_PROPERTY]?.forEach(({ event, handler }) => {
      document.addEventListener(event, handler);
    });
  }, 0);
}

function terminate(el: ClickOutsideElement): void {
  el[HANDLERS_PROPERTY]?.forEach(({ event, handler }) => {
    document.removeEventListener(event, handler);
  });
  delete el[HANDLERS_PROPERTY];
}

const clickOutside: ObjectDirective<ClickOutsideElement, (e: Event) => void> = {
  beforeMount(el, binding: DirectiveBinding<(e: Event) => void>) {
    initialize(el, binding.value);
  },
  updated(el, binding: DirectiveBinding<(e: Event) => void>) {
    terminate(el);
    initialize(el, binding.value);
  },
  unmounted(el) {
    terminate(el);
  },
};

export default clickOutside;
